import { useState, useCallback, useRef, useEffect } from 'react';
import { useOutletContext, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { ChevronDown, AlertTriangle, Bug, Wand2, Plus, FileDown, Play, Pencil, Trash2, Package, XCircle, CheckCircle, History } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTestCases, useDeleteTestCase, useBatchReviewComplete, useNeedsReviewSummary } from '../hooks/useTestCases';
import { useAuthStore } from '../store/authStore';
import { useSpecs } from '../hooks/useSpecifications';
import { useCreateTestRun } from '../hooks/useTestRuns';
import { fetchActiveRun } from '../services/testRunService';
import Spinner from '../components/ui/Spinner';
import TestCaseGenerateModal from '../components/spec/TestCaseGenerateModal';
import ManualTCCreateModal from '../components/testcase/ManualTCCreateModal';
import TCImportModal from '../components/testcase/TCImportModal';
import SpecSelectModal from '../components/testcase/SpecSelectModal';
import TCMoveModal from '../components/testcase/TCMoveModal';
import { usePanelContext } from '../contexts/PanelContext';
import SpecChangeHistoryModal from '../components/testcase/SpecChangeHistoryModal';
import SingleSelectDropdown from '../components/common/SingleSelectDropdown';
import { ProjectTestResultsContent } from './ProjectTestResultsPage';
import type { ProjectDetail } from '../services/projectService';
import type { TestCaseListItem } from '../services/testCaseService';

// --- Label helpers ---

const TYPE_LABEL: Record<string, string> = {
  POSITIVE: 'Positive',
  NEGATIVE: 'Negative',
  BOUNDARY: 'Boundary',
  ALTERNATIVE: 'Alternative',
};

const TYPE_COLOR: Record<string, string> = {
  POSITIVE: 'bg-green-50 text-green-700',
  NEGATIVE: 'bg-red-50 text-red-700',
  BOUNDARY: 'bg-purple-50 text-purple-700',
  ALTERNATIVE: 'bg-blue-50 text-blue-700',
};

const PRIORITY_LABEL: Record<string, string> = {
  BLOCKER: '블로커',
  CRITICAL: '긴급',
  HIGH: '높음',
  MEDIUM: '보통',
  LOW: '낮음',
};

const PRIORITY_COLOR: Record<string, string> = {
  BLOCKER: 'bg-red-100 text-red-800',
  CRITICAL: 'bg-orange-100 text-orange-800',
  HIGH: 'bg-amber-100 text-amber-800',
  MEDIUM: 'bg-blue-100 text-blue-700',
  LOW: 'bg-gray-100 text-gray-600',
};

// --- Main Page ---

export default function TestCaseListPage() {
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  const navigate = useNavigate();

  // REQ-V32-002: 서브탭 (testcases / results)
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSubTab = searchParams.get('tab') === 'results' ? 'results' : 'testcases';

  // Filters (restore specFilter from navigation state)
  const location = useLocation();
  const [specFilter, setSpecFilter] = useState(
    (location.state as { specFilter?: string })?.specFilter ?? '',
  );

  // Specs for dropdown
  const { data: specs } = useSpecs(project.id);

  // Test cases
  const { data: testCases, isLoading } = useTestCases(project.id, {
    specId: specFilter || undefined,
  });

  // Current user & role
  const user = useAuthStore((s) => s.user);
  const myMembership = project.members.find((m) => m.userId === user?.id);
  const canBatchReview = myMembership && ['PROJECT_LEADER', 'PROJECT_MANAGER'].includes(myMembership.projectRole);

  // Mutations
  const createRunMutation = useCreateTestRun(project.id);
  const deleteMutation = useDeleteTestCase(project.id);
  const batchReviewMutation = useBatchReviewComplete(project.id);

  // CHG-022: 재검토 필요 TC 그룹핑
  const { data: reviewSummary } = useNeedsReviewSummary(project.id);
  const [showChangeHistory, setShowChangeHistory] = useState(false);

  // UI state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateSpecId, setGenerateSpecId] = useState('');
  const [showSpecSelector, setShowSpecSelector] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importSpecId, setImportSpecId] = useState('');
  const [isSpecModalOpen, setIsSpecModalOpen] = useState(false);
  const { openDetailPanel, closeDetailPanel, detailPanel } = usePanelContext();
  const [isStartingRun, setIsStartingRun] = useState(false);

  // TC 선택 & 이동
  const [selectedTCIds, setSelectedTCIds] = useState<Set<string>>(new Set());
  const [showMoveModal, setShowMoveModal] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);

  // --- Handlers ---

  const tcList = testCases ?? [];
  const tcCount = tcList.length;
  const hasSpec = !!specFilter;
  const canStartRun = hasSpec && tcCount > 0;
  const needsReviewTCs = tcList.filter((tc) => tc.needsReview);
  const needsReviewCount = needsReviewTCs.length;

  // 전체 선택 체크박스 indeterminate 처리
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedTCIds.size > 0 && selectedTCIds.size < tcCount;
    }
  }, [selectedTCIds.size, tcCount]);

  // 명세 필터 변경 시 선택 초기화
  useEffect(() => {
    setSelectedTCIds(new Set());
  }, [specFilter]);

  const handleSelectAll = useCallback(() => {
    if (selectedTCIds.size === tcCount && tcCount > 0) {
      setSelectedTCIds(new Set());
    } else {
      setSelectedTCIds(new Set(tcList.map((tc) => tc.id)));
    }
  }, [selectedTCIds.size, tcCount, tcList]);

  const handleSelectTC = useCallback((id: string) => {
    setSelectedTCIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleMoveSuccess = useCallback(() => {
    setShowMoveModal(false);
    setSelectedTCIds(new Set());
  }, []);

  const handleStartTestRun = useCallback(async () => {
    if (!specFilter || isStartingRun) return;

    setIsStartingRun(true);
    try {
      const activeRun = await fetchActiveRun(project.id, specFilter);

      if (activeRun) {
        // 진행 중인 실행이 있음
        const confirmed = window.confirm(
          `${activeRun.runNumber}차 테스트가 진행 중입니다. 이어서 실행하시겠습니까?`,
        );
        if (confirmed) {
          navigate(`/projects/${project.projectKey}/specs/${specFilter}/test-runs/${activeRun.runId}`);
        }
      } else {
        // 새 실행 생성
        const specTcCount = testCases?.filter((tc) => tc.specId === specFilter).length ?? tcCount;
        const runList = await import('../services/testRunService').then((m) =>
          m.fetchRunList(project.id, specFilter),
        );
        const nextRunNumber = (runList.length > 0 ? Math.max(...runList.map((r) => r.runNumber)) : 0) + 1;

        const confirmed = window.confirm(
          `${nextRunNumber}차 테스트를 시작하시겠습니까? (현재 등록된 TC ${specTcCount}개)`,
        );
        if (confirmed) {
          const run = await createRunMutation.mutateAsync(specFilter);
          navigate(`/projects/${project.projectKey}/specs/${specFilter}/test-runs/${run.runId}`);
        }
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || '테스트 실행 시작에 실패했습니다.');
    } finally {
      setIsStartingRun(false);
    }
  }, [specFilter, isStartingRun, project.id, project.projectKey, testCases, tcCount, createRunMutation, navigate]);

  const handleDelete = useCallback(
    async (tc: TestCaseListItem) => {
      const confirmed = window.confirm(`"${tc.tcId} ${tc.title}"을(를) 삭제하시겠습니까?`);
      if (!confirmed) return;
      try {
        await deleteMutation.mutateAsync(tc.id);
        toast.success(`${tc.tcId} 삭제 완료`);
        if (detailPanel?.type === 'testcase' && detailPanel.itemId === tc.id) closeDetailPanel();
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(msg || '삭제에 실패했습니다.');
      }
    },
    [deleteMutation, detailPanel, closeDetailPanel],
  );

  const handleBatchReviewComplete = useCallback(async () => {
    const ids = needsReviewTCs.map((tc) => tc.id);
    try {
      const result = await batchReviewMutation.mutateAsync(ids);
      toast.success(result.message || `${ids.length}건 검토완료 처리되었습니다.`);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        toast.error('일괄 검토완료 권한이 없습니다.');
      } else {
        toast.error('일괄 검토완료 처리 중 오류가 발생했습니다.');
      }
    }
  }, [needsReviewTCs, batchReviewMutation]);

  // --- Render ---

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Tab navigation + 액션 버튼 — 고정 영역 */}
      <div className="flex items-center gap-3 pb-4 shrink-0">
        <h1 className="text-xl font-bold text-gray-900">테스트</h1>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setSearchParams({})}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeSubTab === 'testcases'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            테스트케이스
          </button>
          <button
            onClick={() => setSearchParams({ tab: 'results' })}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeSubTab === 'results'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            테스트 결과
          </button>
        </div>

        {/* 액션 버튼 (테스트케이스 탭일 때만) */}
        {activeSubTab === 'testcases' && (
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={handleStartTestRun}
              disabled={!canStartRun || isStartingRun}
              title={
                !hasSpec
                  ? '기능 명세를 먼저 선택해주세요.'
                  : tcCount === 0
                    ? '테스트 케이스를 먼저 등록해주세요.'
                    : undefined
              }
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isStartingRun ? <Spinner className="size-4" /> : <Play className="size-4" />}
              테스트 실행
            </button>
            <button
              onClick={() => setIsSpecModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileDown className="size-4" />
              TC 가져오기
            </button>
            <div className="relative">
              <button
                onClick={() => setShowSpecSelector(!showSpecSelector)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Wand2 className="size-4" />
                체크리스트 생성
                <ChevronDown className="size-3.5" />
              </button>
              {showSpecSelector && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowSpecSelector(false)} />
                  <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 max-h-60 overflow-y-auto">
                    <div className="px-3 py-2 text-sm font-medium text-gray-400 uppercase">기능 명세 선택</div>
                    {(!specs || specs.length === 0) ? (
                      <div className="px-3 py-4 text-sm text-gray-400 text-center">기능 명세가 없습니다.</div>
                    ) : (
                      specs.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => {
                            setGenerateSpecId(s.id);
                            setShowGenerateModal(true);
                            setShowSpecSelector(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                        >
                          <div className="font-medium text-gray-900">{s.functionCode}</div>
                          <div className="text-sm text-gray-500 truncate">{s.title}</div>
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="size-4" />
              TC 추가
            </button>
          </div>
        )}
      </div>

      {/* 스크롤 영역 */}
      <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {/* REQ-V32-002: results 탭이면 전체 테스트 결과 컨텐츠 렌더링 */}
      {activeSubTab === 'results' && (
        <ProjectTestResultsContent projectId={project.id} projectKey={project.projectKey} />
      )}

      {/* testcases 탭 내용 */}
      {activeSubTab === 'testcases' && <>
      {/* Spec filter + TC count */}
      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm text-gray-600">기능 명세별 보기:</label>
        <SingleSelectDropdown
          label="전체"
          value={specFilter}
          onChange={setSpecFilter}
          options={(specs ?? []).map((s) => ({ value: s.id, label: `${s.functionCode} ${s.title}` }))}
        />
        <span className="text-sm text-gray-400">{tcCount}개 테스트케이스</span>
      </div>

      {/* Selection action bar */}
      {selectedTCIds.size > 0 && (
        <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-800">
            {selectedTCIds.size}개 선택됨
          </span>
          <div className="flex items-center gap-2">
            {hasSpec && (
              <button
                onClick={() => setShowMoveModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Package className="size-3.5" />
                명세 이동
              </button>
            )}
            {!hasSpec && (
              <span className="text-sm text-gray-500" title="명세를 선택한 상태에서만 이동할 수 있습니다.">
                명세 필터를 선택해야 이동 가능
              </span>
            )}
            <button
              onClick={() => setSelectedTCIds(new Set())}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <XCircle className="size-3.5" />
              취소
            </button>
          </div>
        </div>
      )}

      {/* Batch review banner */}
      {needsReviewCount > 0 && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <span className="text-sm text-amber-800">
            <AlertTriangle className="size-4 inline-block mr-1.5 -mt-0.5" />
            재검토 필요한 테스트케이스가 <strong>{needsReviewCount}건</strong> 있습니다.
          </span>
          <div className="flex items-center gap-2">
            {/* CHG-022: [변경이력] 버튼 — 모든 멤버에게 노출 */}
            <button
              onClick={() => {
                if (reviewSummary && reviewSummary.groups.length > 0) {
                  setShowChangeHistory(true);
                } else {
                  toast.error('변경이력 정보를 조회할 수 없습니다.');
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <History className="size-4" />
              변경이력
            </button>
            {/* [일괄 검토완료] 버튼 — ADMIN/PM만 노출 */}
            {canBatchReview && (
              <button
                onClick={handleBatchReviewComplete}
                disabled={batchReviewMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-300 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
              >
                <CheckCircle className="size-3.5" />
                {batchReviewMutation.isPending ? '처리 중...' : '일괄 검토완료'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-3 w-10">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={selectedTCIds.size === tcCount && tcCount > 0}
                    onChange={handleSelectAll}
                    className="size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-32">
                  TC ID
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  제목
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-28">
                  유형
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-24">
                  우선순위
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-28">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tcList.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    테스트케이스가 없습니다.
                  </td>
                </tr>
              ) : (
                tcList.map((tc) => (
                  <tr
                    key={tc.id}
                    onClick={() => openDetailPanel('testcase', tc.id, tc)}
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${selectedTCIds.has(tc.id) ? 'bg-blue-50/50' : ''}`}
                  >
                    {/* Checkbox */}
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedTCIds.has(tc.id)}
                        onChange={() => handleSelectTC(tc.id)}
                        className="size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                      />
                    </td>

                    {/* TC ID */}
                    <td className="px-4 py-3 text-gray-900 font-mono text-sm whitespace-nowrap">
                      {tc.tcId}
                    </td>

                    {/* Title */}
                    <td className="px-4 py-3 text-gray-900">
                      <div className="flex items-center gap-2">
                        <span>{tc.title}</span>
                        {tc.needsReview && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">
                            <AlertTriangle className="size-3" />
                            재검토 필요
                          </span>
                        )}
                      </div>
                      {tc.linkedIssues && tc.linkedIssues.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-1">
                          {tc.linkedIssues.map((issue) => (
                            <Link
                              key={issue.id}
                              to={`/projects/${project.id}/issues?search=${issue.issueId}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                            >
                              <Bug className="size-3" />
                              {issue.issueId}
                            </Link>
                          ))}
                          {tc._count.linkedIssues > tc.linkedIssues.length && (
                            <span className="text-[11px] text-gray-400">
                              +{tc._count.linkedIssues - tc.linkedIssues.length}
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${TYPE_COLOR[tc.type] || 'bg-gray-100 text-gray-600'}`}
                      >
                        {TYPE_LABEL[tc.type] || tc.type}
                      </span>
                    </td>

                    {/* Priority */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${PRIORITY_COLOR[tc.priority] || 'bg-gray-100 text-gray-600'}`}
                      >
                        {PRIORITY_LABEL[tc.priority] || tc.priority}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openDetailPanel('testcase', tc.id, tc)}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                          title="편집"
                        >
                          <Pencil className="size-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(tc)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="삭제"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialogs */}
      {showGenerateModal && generateSpecId && (
        <TestCaseGenerateModal
          projectId={project.id}
          specId={generateSpecId}
          onClose={() => {
            setShowGenerateModal(false);
            setGenerateSpecId('');
          }}
        />
      )}

      {showCreateModal && (
        <ManualTCCreateModal
          projectId={project.id}
          specs={specs ?? []}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      <SpecSelectModal
        isOpen={isSpecModalOpen}
        onClose={() => setIsSpecModalOpen(false)}
        onSelect={(specId) => {
          setImportSpecId(specId);
          setShowImportModal(true);
        }}
        projectId={project.id}
        specs={specs ?? []}
      />

      {showImportModal && importSpecId && (
        <TCImportModal
          projectId={project.id}
          specId={importSpecId}
          onClose={() => {
            setShowImportModal(false);
            setImportSpecId('');
          }}
        />
      )}

      {showMoveModal && specFilter && selectedTCIds.size > 0 && (
        <TCMoveModal
          projectId={project.id}
          selectedTCs={tcList.filter((tc) => selectedTCIds.has(tc.id))}
          currentSpecId={specFilter}
          onClose={() => setShowMoveModal(false)}
          onSuccess={handleMoveSuccess}
        />
      )}

      {/* CHG-022: 변경이력 모달 */}
      {reviewSummary && reviewSummary.groups.length > 0 && (
        <SpecChangeHistoryModal
          isOpen={showChangeHistory}
          onClose={() => setShowChangeHistory(false)}
          projectId={project.id}
          groups={reviewSummary.groups}
          onSelectTC={(tcId) => {
            const tc = tcList.find((t) => t.id === tcId);
            if (tc) openDetailPanel('testcase', tc.id, tc);
          }}
        />
      )}
      </>}
      </div>
    </div>
  );
}
